import { useEffect, useState } from "react";

import clsx from "clsx";
import styles from "./C249_FacultyProfileCarousel.module.scss";

import { NextContentfulImage } from "@components/Image";
import Lightbox from "@components/Lightbox/Lightbox";
import Cta from "../Cta/Cta";

import { ContentfulImage } from "@customTypes/ContentfulImage";
import { FacultyCarouselItemTypes } from "@customTypes/FacultyCarouselItemTypes";
import { Nationality } from "@customTypes/Nationality";
import { GeneralLink } from "@customTypes/generalLink";

import FacultyProfileSolo from "./C249_FacultyProfileSolo";

export interface FacultyProfileMultiProps {
  academicFocus?: string;
  associatedPrograms?: string;
  careerHighlight?: string;
  carouselCtaTextLink?: GeneralLink;
  contentTinyMce?: any;
  experience?: any;
  experienceTinyMce?: any;
  facultyProfile?: any;
  facultyProfileData?: {
    fields: {
      profileName?: string;
      profileFirstname?: string;
      profileSurname?: string;
      profilePositionTitle?: string;
    };
  };
  nationality?: Nationality;
  optionalCtaOne?: GeneralLink;
  optionalCtaTwo?: GeneralLink;
  optionalCtaTextLink?: GeneralLink;
  profileName?: string;
  profileDetailsText?: any;
  profileDetailsTextTinyMce?: any;
  profileFirstname?: string;
  profilePicture?: ContentfulImage;
  profilePhoto?: ContentfulImage;
  profilePositionTitle?: string;
  profileProgramme;
  profileSurname?: string;
  profileText?: any;
  profileTextTinyMce?: any;
  recentClients?: string;
  role?: string;
  slug?: string;
  showSubHeading?: boolean;
}
interface CarouselContentProps {
  profilePicture?: string;
  profilePictureDescription: string;
  profileName?: string;
  role?: string;
  associatedPrograms?: string;
  careerHighlight?: string;
  academicFocus?: string;
  recentClients?: string;
  experience?: any;
  experienceTinyMce?: any;
  optionalCtaOne?: GeneralLink;
  optionalCtaTwo?: GeneralLink;
  url?: string;
  nationality?: string;
  optionalCtaTextLink?: GeneralLink;
  showSubHeading?: boolean;
}

const FacultyProfileMulti = (props: {
  content: FacultyProfileMultiProps;
  type: string;
}) => {
  const { content, type } = props;
  const {
    academicFocus,
    associatedPrograms,
    careerHighlight,
    carouselCtaTextLink,
    contentTinyMce,
    experience,
    experienceTinyMce,
    facultyProfile,
    facultyProfileData,
    nationality,
    optionalCtaOne,
    optionalCtaTwo,
    profileName,
    profileDetailsText,
    profileDetailsTextTinyMce,
    profileFirstname,
    profilePicture,
    profilePhoto,
    profilePositionTitle,
    profileProgramme,
    profileSurname,
    profileText,
    profileTextTinyMce,
    recentClients,
    role,
    slug,
    showSubHeading,
  } = content;

  const [carouselContent, setCarouselContent] = useState<CarouselContentProps>(
    // @ts-expect-error expected
    {}
  );
  const title =
    facultyProfileData?.fields?.profilePositionTitle || profilePositionTitle;
  useEffect(() => {
    if (FacultyCarouselItemTypes.FacultyProfileDetail === type) {
      // Faculty
      setCarouselContent({
        profilePicture: profilePhoto?.fields?.file?.url,
        profilePictureDescription: profilePhoto?.fields?.description,
        profileName: facultyProfileData?.fields?.profileName,
        role: title,
        optionalCtaTextLink: carouselCtaTextLink,
        experience: profileDetailsText,
        experienceTinyMce: profileDetailsTextTinyMce,
        url: slug,
      });
    } else if (FacultyCarouselItemTypes.ContributorProfileDetail === type) {
      // Contributor
      setCarouselContent({
        profilePicture: profilePhoto?.fields?.file?.url,
        profilePictureDescription: profilePhoto?.fields?.description,
        profileName: `${profileFirstname} ${profileSurname}`,
        role: title,
        optionalCtaTextLink: carouselCtaTextLink,
        experience: profileDetailsText,
        experienceTinyMce: contentTinyMce,
        url: slug,
      });
    } else if (FacultyCarouselItemTypes.StudentProfileDetail === type) {
      // Student
      setCarouselContent({
        profilePicture: profilePhoto?.fields?.file?.url,
        profilePictureDescription: profilePhoto?.fields?.description,
        profileName: `${profileFirstname} ${profileSurname}`,
        role: profileProgramme?.fields?.programmeTitle,
        optionalCtaTextLink: carouselCtaTextLink,
        experience: profileText,
        experienceTinyMce: profileTextTinyMce,
        url: slug,
      });
    } else if (FacultyCarouselItemTypes.Extended === type) {
      // Extended
      setCarouselContent({
        profilePicture: profilePicture?.fields?.file?.url,
        profilePictureDescription: profilePicture?.fields?.description,
        profileName: facultyProfile?.fields?.facultyProfileData?.fields
          ?.profileName
          ? facultyProfile?.fields?.facultyProfileData?.fields?.profileName
          : profileName,
        role,
        associatedPrograms,
        careerHighlight,
        academicFocus,
        recentClients,
        experience,
        experienceTinyMce,
        optionalCtaOne,
        optionalCtaTwo,
        url: facultyProfile?.fields?.slug,
        nationality: nationality?.fields?.nationality,
        showSubHeading,
      });
    }
  }, []);

  const [showLightbox, setShowLightbox] = useState(false);

  const closeLightbox = () => {
    setTimeout(() => {
      setShowLightbox(false);
    }, 300);
  };

  return (
    <>
      <div className={styles["profile-wrapper"]}>
        <div className={styles["pw-inner-wrapper"]}>
          <div className={styles["image-and-text-wrapper"]}>
            <div className={styles["image-wrapper"]}>
              {carouselContent.profilePicture && (
                <NextContentfulImage
                  src={carouselContent.profilePicture}
                  alt={
                    carouselContent.profilePictureDescription ||
                    carouselContent.profileName
                  }
                  fill
                  sizes="(max-width: 768px) 100vw, (max-width: 1024px) 40vw, 30vw"
                />
              )}
            </div>
            <div className={styles["text-wrapper"]}>
              {carouselContent.profileName && (
                <h3 className={styles["profile-name"]}>
                  {carouselContent.profileName}
                </h3>
              )}
              {carouselContent.role && (
                <p className="role">{carouselContent.role}</p>
              )}
              {carouselContent.associatedPrograms && (
                <>
                  <p
                    className={clsx(
                      styles["programme-type-label"],
                      styles["sub-heading"]
                    )}
                  >
                    Associated Programmes
                  </p>
                  <p className={styles["programme-type"]}>
                    {carouselContent.associatedPrograms}
                  </p>
                </>
              )}
              <div className={styles["button-wrapper"]}>
                {carouselContent?.optionalCtaTextLink ? (
                  <Cta
                    type="tertiary"
                    url={carouselContent.optionalCtaTextLink?.fields?.url}
                    linkText={
                      carouselContent.optionalCtaTextLink?.fields?.linkText
                    }
                    openInNewTab={
                      carouselContent.optionalCtaTextLink?.fields?.openInNewTab
                    }
                    icon="icon-arrow"
                  />
                ) : (
                  <button
                    className={clsx(
                      "cta",
                      "secondary",
                      "no-stretch",
                      styles["promo-cta"]
                    )}
                    onClick={() => {
                      setShowLightbox(true);
                    }}
                  >
                    Show details
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      {showLightbox && (
        <Lightbox
          onCloseFunction={closeLightbox}
          customInnerClass="faculty-profile-carousel"
        >
          {/* @ts-expect-error type mistmatch */}
          <FacultyProfileSolo content={carouselContent} lightbox={true} />
        </Lightbox>
      )}
    </>
  );
};

export default FacultyProfileMulti;
